import { ArrowDownTrayIcon, DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { SaveChanges } from 'components/SaveChanges'
import { COMPANY_NAME_FC, COMPANY_TITLE } from 'config'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import {
  cancelPurchaseAdviceSignRequest,
  downloadFile,
  getPurchaseAdvicePdf,
  getPurchaseAdviceSellerData,
  getUserByCompany,
  openS3Document,
  postPurchaseAdviceSellerData,
  sendPurchaseAdviceSignRequest,
} from 'services'
import { Button, ButtonGroup, PlainTable } from 'stories/components'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { formatDate, getPrice3decimal, InputValidate, Num, openAuditLog, removeComma } from 'utils'
import { isConstructionLoan } from 'utils/isConstructionLoan'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { ConfirmSignDialog } from './ConfirmSignDialog'
import {
  defaultSendSignatureInfos,
  purchaseAdviceAccounting,
  purchaseAdviceGeneralInfos,
  PurchaseAdviceSellerHistory,
} from './constant'

const calcKeys = [
  'PAPremiumOrDiscountCalc',
  'PA_SRPAmountCalc',
  'PAWireAmount',
  'PAInterestDays',
  'PAInterestAmount',
  'InterestSpread',
]

export * from './PurchaseAdviceSellerSign'

const tabs = ['Default', 'Sweep']

export default function PurchaseAdviceSeller() {
  const [_data, _setData] = useState<any>({})
  const [sweepData, setSweepData] = useState<any>({})
  const [overview, setOverview] = useState<any>({})
  const [buyerData, setBuyerData] = useState<any>({})
  const [investors, setInvestors] = useState<Array<any>>([])
  const [action, setAction] = useState('')
  const [purchaseAdviceAccountingFields, setPurchaseAdviceAccountingFields] = useState<any>(purchaseAdviceAccounting)
  const [isDutch, setIsDutch] = useState(false)
  const [changed, setChanged] = useState(false)
  const [isSignRequest, setSignRequest] = useState(false)
  const [history, setHistory] = useState<PurchaseAdviceSellerHistory[]>([])
  const [tab, setTab] = useState('Default')
  const [ovData, setOVData] = useState<any>({})

  const copyPricingFromBuyer = () => {
    const keys = [
      'PAUnpaidPrincipalBalance',
      'PA_SRPPerc',
      'PARollFee',
      'PAOtherFees',
      'PAEscrowBalance',
      'APiece',
      'BPiece',
      'PariPassu',
      'ConstructionReserve',
      'InterestReserve',
      'PABuydownFunds',
      'InvestorCode',
      'FirstPaymentDate',
      'PaidToDate',
      'NextPaymentDueDate',
      'InvestorFirstPaymentDate',
      'LoanPurchasedDate',
      'PANotes',
    ]
    let temp = cloneDeep(data)
    const tabKey = tab === 'Default' ? 'default' : 'sweep'
    keys.map((key) => {
      if (buyerData[tabKey][key] !== undefined) temp[key] = buyerData[tabKey][key]
    })
    setData(temp)
    setChanged(true)
    if (temp.InvestorCode) fetchCompanyInfo(temp.InvestorCode, temp)
  }

  const initData = async () => {
    setAction('init')
    const res = await getPurchaseAdviceSellerData()
    setAction('')
    if (res.success) {
      _setData(res.data.data)
      setSweepData(res.data.sweepData)
      setInvestors(res.data.lenders)
      setBuyerData(res.data.buyerData)
      setHistory(res.data.history)
      if (isConstructionLoan(res.data.data.ProductType)) {
        let tempPostClosing = cloneDeep(purchaseAdviceAccountingFields)
        tempPostClosing['PAUnpaidPrincipalBalance'].span = 2
        tempPostClosing['InterestSpread'].span = 2
        tempPostClosing['InterestCalculation'].visible = true
        setPurchaseAdviceAccountingFields(tempPostClosing)
        if (res.data.data.InterestCalculation === 'Dutch') setIsDutch(true)
      }
    }
  }

  useEffect(() => {
    setLoanNumber()
    initData()
  }, [])

  const data = tab === 'Default' ? _data : sweepData
  const setData = tab === 'Default' ? _setData : setSweepData

  const onSync = async (needSave = true) => {
    setAction('sync')
    needSave && setChanged(false)
    let calcs: any = {}

    calcKeys.map((key) => {
      calcs[key] = calcValue(key)
    })

    const type = tab === 'Default' ? 'info' : 'sweepInfo'
    const json = {
      overview,
      data: {
        ...data,
        ...calcs,
      },
      key: type,
    }

    if (needSave) await postPurchaseAdviceSellerData({ data, key: type })
    setAction('')
    return json
  }

  const download = async () => {
    let calcs: any = {}

    calcKeys.map((key) => {
      calcs[key] = calcValue(key)
    })

    const type = tab === 'Default' ? 'info' : 'sweepInfo'
    const json = {
      overview,
      data: {
        ...data,
        ...calcs,
      },
      key: type,
    }

    const pdfData: Blob = await getPurchaseAdvicePdf(json)
    downloadFile(`${COMPANY_TITLE} Purchase Advice - ${overview.loanNumber}.pdf`, pdfData)
  }

  const feedOverview = async (feed: any) => {
    setOverview(feed)
  }

  const changeData = (key: string, value: any) => {
    const temp = cloneDeep(data)
    if (
      [
        'PARollFee',
        'PAOtherFees',
        'PAEscrowBalance',
        'APiece',
        'BPiece',
        'PariPassu',
        'ConstructionReserve',
        'InterestReserve',
        'PABuydownFunds',
        'PreviouslySwept',
      ].indexOf(key) !== -1
    ) {
      if (removeComma(value) > 0) value = -1 * removeComma(value)
    }
    temp[key] = value
    if (key === 'InterestCalculation') setIsDutch(value === 'Dutch')
    setChanged(true)
    if (defaultSendSignatureInfos[key]) defaultSendSignatureInfos[key].error = ''
    setData(temp)

    if (key == 'InvestorCode' && value) fetchCompanyInfo(value, temp)
  }

  const fetchCompanyInfo = (value: string, data: Record<string, any>) => {
    setAction('gettingAccount')
    getUserByCompany(value)
      .then((res) => {
        if (!res) return
        const newData = cloneDeep(data)
        Object.keys(res).forEach((key) => {
          newData[key] = res[key]
          defaultSendSignatureInfos[key].error = ''
        })
        setData(newData)
      })
      .finally(() => setAction(''))
  }

  const onOpenSignRequest = async () => {
    let hasError = false
    const newData = cloneDeep(data)
    for (const key in defaultSendSignatureInfos) {
      let error = InputValidate({
        ...defaultSendSignatureInfos[key],
        value: data[key] || defaultSendSignatureInfos[key].value,
      })
      newData[key] = data[key] || defaultSendSignatureInfos[key].value
      defaultSendSignatureInfos[key].error = error
      if (error.length > 0) hasError = true
    }
    setData(newData)
    if (hasError) return

    await onSync(true)
    setSignRequest(true)
  }

  const onSendSignRequest = async () => {
    const sendingData: Record<string, any> = {}
    Object.keys(defaultSendSignatureInfos).forEach((key) => (sendingData[key] = data[key]))
    const pdfData = await onSync(false)
    await sendPurchaseAdviceSignRequest({ ...sendingData, pdfData })
    initData()
  }

  const onCancelRequest = async (id: string) => {
    await cancelPurchaseAdviceSignRequest(id)
    initData()
  }

  const calcValue = (key: string) => {
    let rlt = 0
    const unpaidBalance = removeComma(data.PAUnpaidPrincipalBalance)
    if (key === 'PAInterestDays') {
      try {
        const start = DateTime.fromFormat(data['PaidToDate'], 'yyyy-MM-dd')
        const end = DateTime.fromFormat(data['SettlementDate'], 'yyyy-MM-dd')

        rlt = Math.trunc(start.diff(end, 'days').days)
        rlt *= -1
      } catch {}
      return rlt
    }
    if (key === 'PAInterestAmount') {
      try {
        let currentBalance = removeComma(data['PAUnpaidPrincipalBalance'])
        if (isDutch) currentBalance = removeComma(data['OriginalBalance'])
        const rate = removeComma(data['BuyRate'])
        const interestDays = removeComma(calcValue('PAInterestDays'))
        rlt = ((currentBalance * rate) / 100 / 360) * interestDays
      } catch {}
    }
    if (key === 'PAPremiumOrDiscountCalc') {
      const netPrice = removeComma(data.PANetPrice)
      if (netPrice !== 0) {
        rlt = (-1 * unpaidBalance * (100 - netPrice)) / 100
      }
    }
    if (key === 'PA_SRPAmountCalc') {
      const srpPerc = removeComma(data.PA_SRPPerc)
      if (srpPerc !== 0) {
        rlt = (unpaidBalance * srpPerc) / 100
      }
    }
    if (key === 'PAWireAmount') {
      rlt =
        removeComma(data.PAUnpaidPrincipalBalance) +
        removeComma(data.PARollFee) +
        removeComma(data.PAOtherFees) +
        removeComma(data.PAEscrowBalance) +
        removeComma(data.APiece) +
        removeComma(data.BPiece) +
        removeComma(data.PariPassu) +
        removeComma(data.ConstructionReserve) +
        removeComma(data.InterestReserve) +
        removeComma(calcValue('PAInterestAmount')) +
        removeComma(data.PABuydownFunds) +
        Num(data.PreviouslySwept)
      if (removeComma(data.PAPremiumOrDiscountOV) !== 0) {
        rlt += removeComma(data.PAPremiumOrDiscountOV)
      } else {
        rlt += removeComma(calcValue('PAPremiumOrDiscountCalc'))
      }
      if (removeComma(data.PA_SRPAmountOV) !== 0) {
        rlt += removeComma(data.PA_SRPAmountOV)
      } else {
        rlt += removeComma(calcValue('PA_SRPAmountCalc'))
      }
    }
    if (key === 'InterestSpread') {
      return getPrice3decimal(Num(data.NoteRate) - Num(data.BuyRate), false, true)
    }
    return rlt.toFixed(2)
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'PurchaseSeller',
      field: `${key}`,
      keys: {
        field: key,
        type: tab === 'Default' ? 'default' : 'sweep',
      },
    }
    openAuditLog(options)
  }

  const closeOVModal = async (save: boolean, modalData: any) => {
    if (save) {
      changeData(ovData.overrideKey, modalData.ovValue)
      await onSync()
    }
    setOVData({})
  }

  return (
    <div className="purchase-advice-container px-2">
      <SaveChanges show={changed} onSave={onSync} label="Save Changes" />
      <div className="hidden">
        <Overview title="Purchase Advice Seller" feedOverview={feedOverview} />
      </div>
      <div className="max-w-screen-2xl m-auto">
        {/* <div className="relative bg-white shadow1 rounded mb-4 md:p-6 p-2"> */}
        <div className="relative bg-white mt-6">
          <LayoutLoading show={action !== ''} />
          <ButtonGroup title={tabs} value={tab} onChange={setTab} />
          <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5">
              <div className="shadow rounded border">
                <div className="bg-gray-200 round-t py-1 px-4">General Info & Date</div>
                <div className="p-2">
                  {Object.keys(purchaseAdviceGeneralInfos).map((key, index) => {
                    let input: any = purchaseAdviceGeneralInfos[key]
                    input.value = data[key]
                    input.history = true
                    if (key === 'InvestorCode') input.options = investors
                    return (
                      <div className="py-[5px]" key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="shadow rounded border my-4">
                <div className="bg-gray-200 round-t py-1 px-4">Purchase Advice Notes</div>
                <div className="p-2">
                  <RenderInput
                    input={{
                      title: 'Notes',
                      inputType: 'textarea',
                      history: true,
                      value: data['PANotes'] ? data['PANotes'] : '',
                    }}
                    Key={'PANotes'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
              </div>
              <div className="shadow rounded border">
                <div className="bg-gray-200 round-t py-1 px-4">Signature Request</div>
                <div className="p-2">
                  {Object.keys(defaultSendSignatureInfos).map((key, index) => {
                    const input: any = defaultSendSignatureInfos[key]
                    input.history = true
                    input.value = data[key]
                    return (
                      <div className="py-1.5" key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
                <div className="mt-0 flex justify-center">
                  <Button color="gray" loading={action === 'sync'} onClick={onOpenSignRequest}>
                    Save & Send Sign Request
                  </Button>
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="shadow rounded border">
                <div className="bg-gray-200 round-t py-1 px-4">Investor Wire Calculation</div>
                <div className="grid md:grid-cols-6 gap-2 p-2">
                  {Object.keys(purchaseAdviceAccountingFields).map((key, index) => {
                    let input = purchaseAdviceAccountingFields[key]
                    input.history = true
                    input.value = data[key]
                    if (input.visible === false) return null

                    const overrideKey = `${key}OV`
                    let calcValue_result: any = null

                    if (calcKeys.includes(key)) {
                      calcValue_result = calcValue(key)
                      input.value = data[overrideKey] || calcValue_result
                      input.disabled = true

                      const additionalElements = []
                      additionalElements.push(
                        <span
                          className="mx-2 text-shade-blue cursor-pointer"
                          onClick={() => {
                            setOVData({
                              title: input.title,
                              overrideKey,
                              calcValue: calcValue_result || '',
                              ovValue: data[overrideKey] || '',
                              input: { ...input, disabled: false, additionalElements: null, readOnly: false },
                            })
                          }}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>,
                      )

                      input.additionalElements = <div className="flex items-center gap-2">{additionalElements}</div>
                    }

                    if (key === 'PAUnpaidPrincipalBalance')
                      input.title = tab === 'Default' ? 'Current Balance' : 'Sweep Balance '
                    return (
                      <div className={`md:col-span-${input.span ? input.span : 6}`} key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="p-2 grid md:grid-cols-6 gap-2 my-2">
                <div className="md:col-span-2">
                  <RenderInput
                    input={{
                      title: `Taxes to be paid by ${COMPANY_NAME_FC}`,
                      className: 'flex items-center',
                      inputType: 'checkbox',
                      history: true,
                      value: data['TaxPaid'] !== undefined ? data['TaxPaid'] : false,
                    }}
                    Key={'TaxPaid'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
                <div className="md:col-span-2">
                  <RenderInput
                    input={{
                      title: `Insurance to be paid by ${COMPANY_NAME_FC}`,
                      className: 'flex items-center',
                      inputType: 'checkbox',
                      history: true,
                      value: data['InsurancePaid'] !== undefined ? data['InsurancePaid'] : false,
                    }}
                    Key={'InsurancePaid'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
                {tab === 'Sweep' && (
                  <div className="md:col-span-2">
                    <RenderInput
                      input={{
                        title: `Sweep`,
                        className: 'flex items-center',
                        inputType: 'checkbox',
                        history: true,
                        value: data['isSweep'] !== undefined ? data['isSweep'] : false,
                      }}
                      Key={'isSweep'}
                      onChange={changeData}
                      showHistory={showHistory}
                    />
                  </div>
                )}
              </div>
              <div className="mt-0 flex justify-center gap-4">
                <Button color="gray" loading={action === 'sync'} onClick={onSync}>
                  Save
                </Button>
                <Button color="gray" loading={action === 'sync'} onClick={download}>
                  Generate PDF
                </Button>
              </div>

              <div
                className="px-2 flex gap-1 items-center hover:underline cursor-pointer text-shade-blue text-[14.5px]"
                onClick={copyPricingFromBuyer}
              >
                <DocumentDuplicateIcon className="w-4 h-4"></DocumentDuplicateIcon>
                <span>Copy Data from Buyer-Side</span>
              </div>
            </div>
          </div>
          {history.length != 0 && (
            <div className="mt-6 -mb-6">
              <PlainTable
                header={['No', 'Company Name', 'name', 'email', 'By', 'Date', 'Status', 'Action']}
                data={history
                  .sort((a: any, b: any) => {
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  })
                  .map((item, index) => [
                    index + 1,
                    item.companyName,
                    item.name,
                    item.email,
                    item.requestedBy,
                    formatDate(item.createdAt),
                    <span onClick={() => openS3Document(item.signedPdfKey || item.unsignedPdfKey)}>
                      <div
                        className={`cursor-pointer flex items-center underline text-${
                          item.signedPdfKey ? 'green-800' : item.requestCancelled ? 'red-500' : 'gray-900'
                        } hover:text-shade-blue`}
                      >
                        {item.signedPdfKey ? 'Signed' : item.requestCancelled ? 'Cancelled' : 'Unsigned'}
                        <ArrowDownTrayIcon className="w-4 h-4 ml-2" />
                      </div>
                    </span>,
                    item.signedPdfKey || item.requestCancelled ? (
                      ''
                    ) : (
                      <span
                        className="text-red-500 hover:underline cursor-pointer"
                        onClick={() => onCancelRequest(item.id)}
                      >
                        Cancel
                      </span>
                    ),
                  ])}
              />
            </div>
          )}
        </div>
      </div>

      {isSignRequest && (
        <ConfirmSignDialog
          data={data}
          generatePdf={download}
          onClose={() => setSignRequest(false)}
          onConfirm={() => onSendSignRequest()}
        />
      )}
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
          input={ovData.input}
        />
      )}
    </div>
  )
}
